import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import styled, { css, RuleSet } from "styled-components";
import {
  CompassParagraph,
  popAnimation,
  VotingAlignmentBar,
} from "../common/styledComponents";
import {
  CellLink,
  CellType,
  ExpandableTextCell,
  FlaggableVote,
  TableCellType,
} from "../../models/tableModels";
import {
  DeletePortfolioProps,
  DeletePortfolio,
} from "../../pages/MyFunds/DeletePortfolio";
import {
  formatNumber,
  formatReadableDate,
  formatPct,
  toSlug,
  renderTextSample,
} from "../../utils/formattingUtils";
import { PathwayProgressBar, PathwayItem } from "../visuals/PathwayProgressBar";
import { LoadingTableCell } from "./LoadingTableCell";
import {
  darkGreen,
  greyText,
  lightGreyText,
  compassLightBlue,
  votingAlignmentBarsPalette,
} from "../../styles";
import {
  ElementContainer,
  FlexibleContainer,
} from "../containers/styledComponents";
import { CompanyLogo, CompanyLogoProps } from "./CompanyLogo";
import {
  CustomDropdown,
  CustomDropdownProps,
} from "../visuals/PageFilter/PageFilter";
import { CompassTag } from "../visuals/CompassTag";
import { TagProps } from "../visuals/CompassTag";
import { MESSAGES } from "../../messages";
import { ROUTES } from "../../routes/routeNames";
import {
  Avatar,
  AvatarMatrix,
  AvatarMatrixProps,
  AvatarProps,
  AvatarStack,
} from "../visuals/Avatar";
import { PriorityIndicator } from "../visuals/PriorityIndicator";

import {
  TitleWithCircleTag,
  TitleWithCircleTagProps,
} from "./TitleWithCircleTag";
import { NoteDropdownProps, TaskPriority } from "../../models/workflowModels";
import { DeleteItem, DeleteItemProps } from "./DeleteItem";
import { NoteDropdown } from "../../pages/Workspace/NoteDropdown";
import { CountryWFlag } from "../visuals/CountryWFlag";
import { ProgressBar } from "react-bootstrap";
import {
  FundManagerBadgeList,
  FundManagerBadgeProps,
} from "../visuals/FundManagerBadgeList";

const RationaleOverlay = styled.div<{
  overlayBg: string;
  overlayColor: string;
}>`
  position: absolute;
  top: -6px;
  right: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ overlayBg }): string => overlayBg};
  color: ${({ overlayColor }): string => overlayColor};
  font-size: 8px;
  pointer-events: none;
  z-index: 1;
`;

const VoteBadgeWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

// Styled component for animating CompassParagraph
const TableCompassParagraph = styled(CompassParagraph)<{
  animate?: boolean;
  isLink?: boolean;
}>`
  display: inline-block;
  ${(props): false | RuleSet<object> | undefined =>
    props.animate &&
    css`
      animation: ${popAnimation} 0.4s ease-out;
    `}

  &:hover {
    text-decoration: ${({ isLink = false }): string =>
      isLink ? "underline" : "none"};
  }
`;

// Styled components for expandable text cell
const ExpandableTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 15px;
  max-width: 300px;
`;

const TruncatedCellText = styled(CompassParagraph)<{
  maxLines: number;
  isExpanded: boolean;
}>`
  margin: 0;
  color: ${greyText};
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({ isExpanded, maxLines }): string =>
    isExpanded ? "unset" : String(maxLines)};
  -webkit-box-orient: vertical;
`;

const ExpandToggle = styled.span`
  color: ${compassLightBlue};
  font-size: 11px;
  cursor: pointer;
  font-family: "Apercu";
  &:hover {
    text-decoration: underline;
  }
`;

export const CustomTableCell: React.FC<CustomTableCellProps> = ({
  type,
  value,
  fontColor = darkGreen,
}) => {
  const [animate, setAnimate] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  // Trigger animation on value change
  useEffect(() => {
    if (value !== undefined) {
      // Ensures animation triggers only if value is not undefined
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 600); // Reset animation state after 0.6 seconds
      return () => clearTimeout(timer);
    }
  }, [value]);

  // Helper function to render content based on the type
  const renderContent = (): JSX.Element => {
    switch (type) {
      case TableCellType.TEXT:
      case TableCellType.NUMBER:
      case TableCellType.PERCENTAGE:
        const formattedValue =
          type === TableCellType.NUMBER
            ? formatNumber(value as number)
            : type === TableCellType.PERCENTAGE
              ? formatPct(value as number)
              : value;
        return (
          <TableCompassParagraph ml={15} color={fontColor} animate={animate}>
            {formattedValue as string}
          </TableCompassParagraph>
        );

      case TableCellType.TITLE:
        return (
          <CompassParagraph fontSize={14} ml={16}>
            {(value as string) || MESSAGES.NOT_AVAILABLE}
          </CompassParagraph>
        );
      case TableCellType.PATHWAY:
        return (
          <FlexibleContainer ml={"15px"} mt={"-20px"}>
            <PathwayProgressBar pathwayItem={value as PathwayItem} />
          </FlexibleContainer>
        );
      case TableCellType.NUMBER_W_BULLET_CHART:
        return <div>Bullet Chart for {value as string}</div>;
      case TableCellType.LOADING:
        return <LoadingTableCell />;
      case TableCellType.DATE:
        return (
          <TableCompassParagraph ml={15} color={fontColor} animate={animate}>
            {formatReadableDate(value as string)}
          </TableCompassParagraph>
        );
      case TableCellType.DELETE_PORTFOLIO:
        return <DeletePortfolio {...(value as DeletePortfolioProps)} />;

      case TableCellType.DELETE:
        return <DeleteItem {...(value as DeleteItemProps)} />;

      case TableCellType.DROPDOWN:
        return <CustomDropdown {...(value as CustomDropdownProps)} />;
      case TableCellType.COMPANY_LOGO:
        return (
          <ElementContainer ml={15}>
            <CompanyLogo {...(value as CompanyLogoProps)} />
          </ElementContainer>
        );

      case TableCellType.VOTING_ALIGNMENT_BAR:
        const votingAlignment = value as number;

        return (
          <ElementContainer flexDirection="column">
            <CompassParagraph color={lightGreyText} fontSize={12}>
              {`${formatPct(votingAlignment, true)}`}
            </CompassParagraph>
            <VotingAlignmentBar barHeight={4}>
              {[0, 1].map((index) => (
                <ProgressBar
                  variant="custom"
                  now={
                    index === 0
                      ? votingAlignment * 100
                      : (1 - votingAlignment) * 100
                  }
                  key={index}
                  style={{ backgroundColor: votingAlignmentBarsPalette[index] }}
                />
              ))}
            </VotingAlignmentBar>
          </ElementContainer>
        );
      case TableCellType.TAG:
        return (
          <FlexibleContainer ml={"15px"} jc="flex-start" ai="center">
            <CompassTag {...(value as TagProps)} />

            {
              <TableCompassParagraph>
                {(value as TagProps).displayValue as string}
              </TableCompassParagraph>
            }
          </FlexibleContainer>
        );

      case TableCellType.TAGS:
        return (
          <FlexibleContainer fw="wrap" ml={"15px"} jc="flex-start" ai="center">
            {(value as TagProps[]).map((tag) => (
              <FlexibleContainer key={tag.tagKey} mb="3px" mr="0">
                <CompassTag {...tag} />
              </FlexibleContainer>
            ))}
          </FlexibleContainer>
        );

      case TableCellType.FUND_MANAGER_BADGES:
        return (
          <FlexibleContainer w="100%" ml="15px" jc="flex-start" ai="flex-start">
            <FundManagerBadgeList items={value as FundManagerBadgeProps[]} />
          </FlexibleContainer>
        );

      case TableCellType.COUNTRY_W_FLAG:
        return (
          <FlexibleContainer fw="wrap" jc="flex-start" ai="center" ml="15px">
            <CountryWFlag countryIso2={value as string} />
          </FlexibleContainer>
        );

      case TableCellType.AVATAR:
        return (
          <FlexibleContainer fw="wrap" ml={"15px"} jc="flex-start" ai="center">
            <Avatar {...(value as AvatarProps)} />
          </FlexibleContainer>
        );

      case TableCellType.AVATAR_MATRIX:
        return (
          <FlexibleContainer fw="wrap" ml="15px" jc="flex-start" ai="center">
            <AvatarMatrix {...(value as AvatarMatrixProps)} />
          </FlexibleContainer>
        );

      case TableCellType.AVATAR_STACK:
        return (
          <FlexibleContainer fw="wrap" ml={"15px"} jc="flex-start" ai="center">
            <AvatarStack items={value as AvatarProps[]} />
          </FlexibleContainer>
        );

      case TableCellType.PRIORITY:
        return (
          <FlexibleContainer fw="wrap" ml={"15px"} jc="flex-start" ai="center">
            <PriorityIndicator priority={value as TaskPriority} />
          </FlexibleContainer>
        );

      case TableCellType.CIRCLE_BADGE_TITLE:
        return (
          <FlexibleContainer ml={"15px"} ai="center" jc="flex-start">
            <TitleWithCircleTag {...(value as TitleWithCircleTagProps)} />
          </FlexibleContainer>
        );

      case TableCellType.FUND_LINK:
        const fundSlug = toSlug(value as string);
        const link = `${ROUTES.SCREENING_TOOL}/${fundSlug}`;
        return (
          <a href={link}>
            <TableCompassParagraph
              ml={15}
              color={fontColor}
              animate={animate}
              isLink={true}
              cursor="pointer"
            >
              {value as string}
            </TableCompassParagraph>
          </a>
        );

      case TableCellType.LINK:
        const params = value as CellLink;
        if (params.newTab) {
          return (
            <a href={params.link} target="_blank" rel="noopener noreferrer">
              <TableCompassParagraph
                ml={15}
                color={fontColor}
                animate={animate}
                isLink={true}
                cursor="pointer"
                hoverUnderline={true}
              >
                {params.displayValue}
              </TableCompassParagraph>
            </a>
          );
        }
        return (
          <TableCompassParagraph
            ml={15}
            color={fontColor}
            animate={animate}
            isLink={true}
            cursor="pointer"
            hoverUnderline={true}
            onClick={(): void => navigate(params.link)}
          >
            {params.displayValue}
          </TableCompassParagraph>
        );

      case TableCellType.SELECT:
        const dropdownCustomProps = value as NoteDropdownProps;
        return <NoteDropdown {...dropdownCustomProps} />;

      case TableCellType.FLAGGABLE_VOTE:
        const flaggableVote = value as FlaggableVote;
        const { displayVotingRecordModal, recordId } =
          flaggableVote.displayVotingRecordModalParams;
        const tagProps = value as TagProps;
        const badgeContent = (
          <VoteBadgeWrapper>
            <CompassTag {...tagProps} isClickable={true} />
            {flaggableVote.hasRationale && (
              <RationaleOverlay
                overlayBg={tagProps.bg}
                overlayColor={tagProps.color}
              >
                <FileTextOutlined />
              </RationaleOverlay>
            )}
          </VoteBadgeWrapper>
        );
        return (
          <ElementContainer ml={20} ai="center">
            <FlexibleContainer
              mr="-15px"
              onClick={(): void => {
                displayVotingRecordModal(recordId);
              }}
            >
              {flaggableVote.hasRationale ? (
                <Tooltip
                  title={
                    renderTextSample(flaggableVote.rationale || "", 100) ||
                    MESSAGES.NOT_AVAILABLE
                  }
                >
                  {badgeContent}
                </Tooltip>
              ) : (
                badgeContent
              )}
            </FlexibleContainer>

            <CustomDropdown {...(value as FlaggableVote)} />
          </ElementContainer>
        );
      case TableCellType.EXPANDABLE_TEXT:
        const expandable = value as ExpandableTextCell;
        const maxLines = expandable.maxLines || 2;
        if (!expandable.text || expandable.text === "-") {
          return (
            <TableCompassParagraph ml={15} color={greyText}>
              {expandable.text || MESSAGES.NOT_AVAILABLE}
            </TableCompassParagraph>
          );
        }
        return (
          <ExpandableTextWrapper>
            <TruncatedCellText maxLines={maxLines} isExpanded={isExpanded}>
              {expandable.text}
            </TruncatedCellText>
            <ExpandToggle onClick={(): void => setIsExpanded(!isExpanded)}>
              {isExpanded ? "Show less" : "Show more"}
            </ExpandToggle>
          </ExpandableTextWrapper>
        );

      default:
        return (
          <TableCompassParagraph ml={15} color={fontColor} animate={animate}>
            {(value as string) || MESSAGES.NOT_AVAILABLE}
          </TableCompassParagraph>
        );
    }
  };

  return <>{renderContent()}</>;
};

export type CustomTableCellProps = {
  type: TableCellType;
  value: CellType;
  fontColor?: string;
};
